import { Stack, useTheme } from '@mui/material';
import { useIsMutating, useMutation } from '@tanstack/react-query';
import { useNotify, useTranslate } from 'ra-core';
import { memo, useState } from 'react';
import { Confirm, FileField, FileInputClasses } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { ImageInput, ImageInputProps } from '../../../../components/inputs/ImageInput';
import { xgacDataProvider } from '../../../../dataProviders/xgacDataProvider';
import { notifyWithError } from '../../../../utils/errorHandling';
import { DocumentProcessingSpinner, getDocumentProcessingBorderStyles } from '../DocumentProcessingIndicators';

type InputSources = {
  source: 'document';
  lastReceivedOnSource: 'lastReceivedOn';
  expiryDateSource: 'expiryDate';
} | {
  source: `certificationsByAssetId.${string}.document`;
  lastReceivedOnSource: `certificationsByAssetId.${string}.lastReceivedOn`;
  expiryDateSource: `certificationsByAssetId.${string}.expiryDate`;
};

type CertificationDocumentInputProps = Omit<ImageInputProps, 'accept' | 'onChange'> & InputSources;

const CERTIFICATE_DOCUMENT_ANALYSIS_CONSENT_KEY = 'certificateDocumentAnalysisConsent';
export const CERTIFICATE_DOCUMENT_ANALYSIS_MUTATION_KEY = 'certificateDocumentAnalysis';

export const useCertificateAnalysisPending = (source?: InputSources['source']) => {

  return useIsMutating({
    mutationKey: source
      ? [CERTIFICATE_DOCUMENT_ANALYSIS_MUTATION_KEY, source]
      : [CERTIFICATE_DOCUMENT_ANALYSIS_MUTATION_KEY],
    exact: Boolean(source),
  }) > 0;

};

export const CertificationDocumentInput = memo((props: CertificationDocumentInputProps) => {

  const theme = useTheme();
  const translate = useTranslate();
  const notify = useNotify();
  const { resetField, setValue } = useFormContext();

  const [pendingDocument, setPendingDocument] = useState<File | null>(null);

  const documentAnalysisMutation = useMutation<Awaited<ReturnType<typeof xgacDataProvider['analyseCertificateDocument']>>, Error, File>({
    mutationKey: [CERTIFICATE_DOCUMENT_ANALYSIS_MUTATION_KEY, props.source],
    mutationFn: async (file: File) => {

      return xgacDataProvider.analyseCertificateDocument(file);

    },
    retry: false,
  });

  const analyseDocument = async (file: File) => {

    try {

      const result = await documentAnalysisMutation.mutateAsync(file);
      setValue(props.lastReceivedOnSource, result.lastReceivedOn, { shouldDirty: true, shouldValidate: true });
      setValue(props.expiryDateSource, result.expiryDate, { shouldDirty: true, shouldValidate: true });

    } catch (error) {

      notifyWithError(notify, error);

    } finally {

      setPendingDocument(null);

    }

  };

  const handleDocumentSelected = (file: File | null | undefined) => {

    if (!file) {

      setPendingDocument(null);
      resetField(props.lastReceivedOnSource);
      resetField(props.expiryDateSource);
      return;

    }

    const consent = sessionStorage.getItem(CERTIFICATE_DOCUMENT_ANALYSIS_CONSENT_KEY);

    if (consent !== 'true') {

      setPendingDocument(file);
      return;

    }

    analyseDocument(file);

  };

  const grantConsent = () => {

    const file = pendingDocument;

    if (!file) return;

    sessionStorage.setItem(CERTIFICATE_DOCUMENT_ANALYSIS_CONSENT_KEY, 'true');
    setPendingDocument(null);
    analyseDocument(file);

  };

  const handleConsentDialogClose = (_: unknown, reason?: string) => {

    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;

    sessionStorage.setItem(CERTIFICATE_DOCUMENT_ANALYSIS_CONSENT_KEY, 'false');
    setPendingDocument(null);

  };

  return (
    <>
      <ImageInput
        {...props}
        accept={{
          'application/pdf': ['.pdf'],
          'image/png': ['.png'],
          'image/jpeg': ['.jpg', '.jpeg'],
        }}
        onChange={handleDocumentSelected}
        readOnly={documentAnalysisMutation.isPending}
        placeholder={documentAnalysisMutation.isPending ? (
          <Stack
            role="status"
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{ minHeight: '1.5em', my: 2 }}
          >
            <DocumentProcessingSpinner />
            <span>
              {translate('resources.assets.certifications.text.ai_document_analysis_processing')}
            </span>
          </Stack>
        ) : undefined}
        removeIcon={() => (
          <span style={{
            fontSize: '12px',
            fontWeight: 'bold',
            color: theme.palette.error.main,
            textDecoration: 'underline',
            padding: 2,
          }}>
            {translate('ra.input.image.remove_current')}
          </span>
        )}
        sx={{
          [`& .${FileInputClasses.dropZone}`]: {
            position: 'relative',
            borderStyle: documentAnalysisMutation.isPending ? 'solid' : 'dashed',
            borderColor: documentAnalysisMutation.isPending ? 'transparent' : undefined,
            '&::after': getDocumentProcessingBorderStyles({
              theme,
              isProcessing: documentAnalysisMutation.isPending,
              padding: '1px',
            }),
          },
          [`& .previews .${FileInputClasses.removeButton}`]: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1,
            paddingTop: 1,
          },
          [`& .previews .${FileInputClasses.removeButton} button`]: {
            display: documentAnalysisMutation.isPending ? 'none' : undefined,
            opacity: 1,
            position: 'static',
            top: 'unset',
            right: 'unset',
            order: 2,
            padding: 0,
            '&:hover': {
              borderRadius: 1,
            },
          },
          '& .previews .RaFileField-root': {
            order: 1,
            flex: 'none',
            minWidth: 0,
          },
          ...props.sx,
        }}
      >
        <FileField source="src" title="title" />
      </ImageInput>
      <Confirm
        isOpen={pendingDocument !== null}
        title="resources.assets.certifications.text.ai_document_analysis_consent_title"
        content="resources.assets.certifications.text.ai_document_analysis_consent_content"
        confirm="resources.assets.certifications.text.ai_document_analysis_consent_confirm"
        cancel="resources.assets.certifications.text.ai_document_analysis_consent_reject"
        onConfirm={grantConsent}
        onClose={handleConsentDialogClose}
        sx={{
          '& .MuiDialogActions-root': {
            justifyContent: 'space-between',
          },
        }}
      />
    </>
  );

});
